import { BarChart as BarChartIcon } from '@mui/icons-material';
import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { formatMoney } from '../../../../../../../lib/functions/utils';

interface FundsComparisonChartProps {
  data: any;
  overdueData?: any;
}

const FundsComparisonChart: React.FC<FundsComparisonChartProps> = ({
  data,
  overdueData,
}) => {
  const [chartType, setChartType] = useState<'amounts' | 'counts' | 'overdue'>(
    'amounts',
  );
  const elementRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(420);
  const [chartWidth, setChartWidth] = useState(0);

  if (!data?.nomineesByFund || data.nomineesByFund.length === 0) {
    return (
      <Paper
        sx={{
          p: 3,
          border: '1px solid #e0e0e0',
          borderRadius: '10px',
          backgroundColor: '#ffffff',
          height: 300,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Comparación por Fondos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Distribución del monto total de reventas por fondo
        </Typography>
        <Box
          sx={{
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No hay datos disponibles para mostrar la comparación por fondos
          </Typography>
        </Box>
      </Paper>
    );
  }

  const handleChartTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    newType: 'amounts' | 'counts' | 'overdue' | null,
  ) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  const chartData = useMemo(() => {
    return data.nomineesByFund.map((fund: any, index: number) => ({
      id: index,
      fundName: fund.fundName,
      nominees: fund.nominees,
      resells: fund.resells,
      amount: fund.amount,
      overdueAmount:
        overdueData?.byFund?.find(
          (overdueFund: any) => overdueFund.fundName === fund.fundName,
        )?.overdueAmount || 0,
    }));
  }, [data.nomineesByFund, overdueData?.byFund]);

  const isAmountsView = chartType === 'amounts';
  const isCountsView = chartType === 'counts';
  const isOverdueView = chartType === 'overdue';

  // Configuración del eje X
  const xAxis = [
    {
      dataKey: 'fundName',
      scaleType: 'band' as const,
      tickLabelStyle: {
        fontSize: 11,
        angle: -45,
        textAnchor: 'end' as const,
      },
      tickSize: 6,
    },
  ];

  // Configuración del eje Y
  const yAxis = [
    {
      scaleType: 'linear' as const,
      tickLabelStyle: {
        fontSize: 12,
      },
      valueFormatter: (value: number) => {
        if (isAmountsView || isOverdueView) {
          if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
          return value.toString();
        } else {
          return value.toString();
        }
      },
      min: 0,
      max: undefined,
      tickNumber: 5,
    },
  ];

  // Configuración de las series
  const series = isAmountsView
    ? [
        {
          dataKey: 'amount',
          label: 'Monto Total',
          color: '#03B4DC',
          valueFormatter: (value: number | null) =>
            value !== null ? `${formatMoney(value)}` : '',
        },
      ]
    : isCountsView
      ? [
          {
            dataKey: 'nominees',
            label: 'Nóminas Creadas',
            color: '#0F23DF',
          },
          {
            dataKey: 'resells',
            label: 'Total Reventas',
            color: '#07D2FE',
          },
        ]
      : [
          {
            dataKey: 'overdueAmount',
            label: 'Monto en Mora',
            color: '#d32f2f',
            valueFormatter: (value: number | null) =>
              value !== null ? `${formatMoney(value)}` : '',
          },
        ];

  useEffect(() => {
    const updateDimensions = () => {
      if (elementRef.current) {
        const { width } = elementRef.current.getBoundingClientRect();
        setChartWidth(width - 40);
        setChartHeight(420);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const getDescription = () => {
    if (isAmountsView)
      return 'Distribución del monto total de reventas por fondo';
    if (isCountsView)
      return 'Distribución de nóminas creadas y reventas totales por fondo';
    return 'Distribución del monto en mora por fondo';
  };

  return (
    <Paper
      sx={{
        p: 3,
        border: '1px solid #e0e0e0',
        borderRadius: '10px',
        backgroundColor: '#ffffff',
        height: 580,
      }}
      ref={elementRef}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isOverdueView
              ? 'rgba(211, 47, 47, 0.12)'
              : 'rgba(3, 180, 220, 0.12)',
          }}
        >
          <BarChartIcon
            sx={{
              fontSize: 18,
              color: isOverdueView ? '#d32f2f' : '#03B4DC',
            }}
          />
        </div>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.1rem',
          }}
        >
          Comparación por Fondos
        </Typography>
      </div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {getDescription()}
      </Typography>

      {/* Toggle Buttons */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '120px',
            },
          }}
        >
          <ToggleButton value="amounts">Montos</ToggleButton>
          <ToggleButton value="counts">Nóminas y Reventas</ToggleButton>
          <ToggleButton value="overdue">Mora</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Chart */}
      <Box sx={{ height: 450, width: '100%' }}>
        <BarChart
          dataset={chartData}
          xAxis={xAxis}
          yAxis={yAxis}
          series={series}
          height={chartHeight}
          width={chartWidth}
          margin={{ top: 20, right: 60, left: 60, bottom: 120 }}
          borderRadius={6}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default FundsComparisonChart;
